import type {
  AnimationEvent,
  AnimationPlayer,
  BallState,
  PitchSnapshot,
  PositionChange,
} from "@/types"

import type { PitchPlayer, PitchState } from "./use-pitch-state"

// ─── Pure helpers ───────────────────────────────────────────────────────────
//
// Stateless functions for transforming PitchState. Shared by the main replay
// reducer (use-replay-reducer) and the mini-replay reducer
// (use-mini-replay-reducer).

/**
 * Applies a single animation event to a pitch state, returning the next pitch
 * state. Exported so reducers and seek implementations can replay events from
 * scratch without going through a history stack.
 */
export function applyEventToPitchState(
  state: PitchState,
  event: AnimationEvent,
  players: Record<string, AnimationPlayer>,
): PitchState {
  // KickoffEvent with pitchSnapshot → full state reset from snapshot
  if (event.pitchSnapshot) {
    return buildPitchStateFromSnapshot(event.pitchSnapshot, players)
  }

  // Otherwise apply positionChanges in sequence
  let current: PitchState = {
    playerPositions: new Map(state.playerPositions),
    ballPosition: state.ballPosition,
  }

  for (const change of event.positionChanges) {
    current = applyPositionChange(current, change)
  }

  return current
}

export function buildPitchStateFromSnapshot(
  snapshot: PitchSnapshot,
  players: Record<string, AnimationPlayer>,
): PitchState {
  const playerPositions = new Map<string, PitchPlayer>()
  const { players: snapshotPlayers, ball } = snapshot
  const ballPosition = ballCoords(ball)

  for (const sp of snapshotPlayers) {
    const key = `${sp.x},${sp.y}`
    const playerData = players[sp.id]

    playerPositions.set(key, {
      playerId: sp.id,
      abbr: playerData?.abbr ?? "?",
      teamId: sp.teamId,
      status: normalizeStatus(sp.status),
      hasBall: ball?.carrierId === sp.id,
    })
  }

  return { playerPositions, ballPosition }
}

export function applyPositionChange(
  state: PitchState,
  change: PositionChange,
): PitchState {
  const positions = new Map(state.playerPositions)
  let ballPosition = state.ballPosition

  if (
    change.type === "move" ||
    change.type === "push" ||
    change.type === "follow_up"
  ) {
    // Reposition token: remove from old key, place at new key
    if (change.from && change.to && Array.isArray(change.to)) {
      const fromKey = `${change.from[0]},${change.from[1]}`
      const to = change.to
      const toKey = `${to[0]},${to[1]}`
      const player = positions.get(fromKey)
      if (player) {
        positions.delete(fromKey)
        positions.set(toKey, player)
        // Ball follows the carrier
        if (player.hasBall) {
          ballPosition = [to[0], to[1]]
        }
      }
    }
  } else if (change.type === "status") {
    // Update status by playerId
    if (change.playerId) {
      for (const [key, player] of positions) {
        if (player.playerId === change.playerId) {
          positions.set(key, {
            ...player,
            status: normalizeStatus(change.status),
          })
          break
        }
      }
    }
  } else if (change.type === "ball") {
    const to = change.to
    const resolved = ballChangeCoords(to)
    if (resolved) {
      // Clear existing carrier
      for (const [key, player] of positions) {
        if (player.hasBall) {
          positions.set(key, { ...player, hasBall: false })
        }
      }
      ballPosition = resolved
      // Mark new carrier if present
      const carrierId = isBallState(to) ? to.carrierId : undefined
      if (carrierId) {
        for (const [key, player] of positions) {
          if (player.playerId === carrierId) {
            positions.set(key, { ...player, hasBall: true })
            break
          }
        }
      }
    }
  }

  return { playerPositions: positions, ballPosition }
}

function ballCoords(ball: BallState | null): [number, number] | null {
  if (!ball) return null
  return [ball.x, ball.y]
}

function isBallState(to: unknown): to is BallState {
  return !!to && typeof to === "object" && "x" in to && "y" in to
}

function ballChangeCoords(
  to: [number, number] | BallState | undefined,
): [number, number] | null {
  if (!to) return null
  if (Array.isArray(to)) return to
  if (isBallState(to)) return [to.x, to.y]
  return null
}

function normalizeStatus(
  status: string | undefined,
): "standing" | "prone" | "stunned" {
  if (status === "prone") return "prone"
  if (status === "stunned") return "stunned"
  return "standing"
}
